import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LandingFooter from '../components/LandingFooter';
import LandingTopNav from '../components/LandingTopNav';
import HomeThreadPreview from '../components/HomeThreadPreview';
import useRailMarker from '../hooks/useRailMarker';
import {
  Sparkles,
  Camera,
  FileText,
  Palette,
  Edit3,
  Tags,
  RotateCcw,
  Shuffle,
  Layers,
  Flame,
  ArrowRight,
  ChevronDown
} from 'lucide-react';
import './HomePage.css';
import './HomePageSections.css';
import home from '../content/landing/home.json';

// Icon registry — maps icon names referenced in home.json to lucide-react
// components. The JSON is the source of truth for tool order and copy; this
// map only resolves the string into a renderable component.
const ICON_BY_NAME = {
  Camera, FileText, Palette, Layers, Sparkles, Edit3, Tags, RotateCcw, Shuffle,
};

// Journey chapter chips reference tools by display name.
const ICON_BY_TOOL = {
  'AI Designer': Sparkles,
  'Design Variations': Shuffle,
  'Garment Editor': Edit3,
  'Color Changer': Palette,
  'Fabric Swapper': Layers,
  'Tech Pack Generator': FileText,
  'AI Photoshoots': Camera,
  'Lookbook Creator': RotateCcw,
  'Product Tagger': Tags,
};

function useHomeMetadata(meta, faq) {
  useEffect(() => {
    // Helmet 6 registers before mount and loses that registration during
    // React 19's StrictMode remount. Keep this fix local to the homepage.
    // Run after App's deferred canonical update, then use Helmet's ownership
    // marker so the next route can replace these tags normally.
    const managed = [];
    const frame = window.requestAnimationFrame(() => {
      document.title = meta.title;
      const setTag = (selector, tagName, attributes, text) => {
        const existing = [...document.head.querySelectorAll(selector)];
        const element = existing.shift() || document.createElement(tagName);
        existing.forEach((duplicate) => duplicate.remove());
        Object.entries(attributes).forEach(([name, value]) =>
          element.setAttribute(name, value)
        );
        element.setAttribute('data-react-helmet', 'true');
        if (text !== undefined) element.textContent = text;
        if (!element.parentNode) document.head.appendChild(element);
        managed.push(element);
      };
      setTag('meta[name="description"]', 'meta', {
        name: 'description',
        content: meta.description,
      });
      setTag('link[rel="canonical"]', 'link', {
        rel: 'canonical',
        href: meta.canonical,
      });
      const social = {
        'og:title': meta.title,
        'og:description': meta.description,
        'og:type': 'website',
        'og:url': meta.canonical,
        'og:image': new URL(meta.ogImage, meta.canonical).href,
        'twitter:card': 'summary_large_image',
        'twitter:image': new URL(meta.ogImage, meta.canonical).href,
      };
      Object.entries(social).forEach(([key, content]) => {
        const attribute = key.startsWith('og:') ? 'property' : 'name';
        setTag(`meta[${attribute}="${key}"]`, 'meta', {
          [attribute]: key,
          content,
        });
      });
      const graph = [
        ...(meta.jsonLd || []),
        {
          '@type': 'FAQPage',
          mainEntity: faq.items.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        },
      ];
      setTag(
        'script[type="application/ld+json"]',
        'script',
        {
          type: 'application/ld+json',
        },
        JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })
      );
    });
    return () => {
      window.cancelAnimationFrame(frame);
      managed.forEach((element) => element.remove());
    };
  }, [meta, faq]);
}


const HomePage = () => {
  const navigate = useNavigate();

  const heroImages = home.hero.images;
  const [currentHeroImage, setCurrentHeroImage] = useState(0);
  const [heroFramesReady, setHeroFramesReady] = useState(false);
  const loadedFrames = useRef(new Set());
  const [reduceMotion, setReduceMotion] = useState(false);

  // Journey rail: how many chapter dots the marker has passed. The marker
  // itself is driven DOM-directly by useRailMarker (no re-renders per frame).
  const [activeCount, setActiveCount] = useState(0);
  const railWrapRef = useRef(null);
  const railMarkerRef = useRef(null);
  const chapterRefs = useRef([]);

  // The original fashion crossfade stays intact. Only advance to a decoded
  // image, and leave the first composition still for reduced-motion users.
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      setReduceMotion(query.matches);
      if (query.matches) setCurrentHeroImage(0);
    };
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const timer = setTimeout(() => setHeroFramesReady(true), 1200);
    return () => clearTimeout(timer);
  }, [reduceMotion]);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const interval = setInterval(() => {
      if (document.hidden) return;
      setCurrentHeroImage((current) => {
        const next = (current + 1) % heroImages.length;
        return loadedFrames.current.has(next) ? next : current;
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [reduceMotion, heroImages.length]);

  const markHeroLoaded = async (image, index) => {
    if (image.decode) {
      try { await image.decode(); } catch { return; }
    }
    loadedFrames.current.add(index);
  };

  // Smooth rail marker + chapter-dot activation. setActiveCount is guarded by
  // equality, so React re-renders only when a dot flips (max 6 times per pass).
  useRailMarker(railWrapRef, railMarkerRef, {
    onFrame: (yCenter, railRect) => {
      const markerY = railRect.top + yCenter;
      let active = 0;
      chapterRefs.current.forEach((el) => {
        if (el && el.getBoundingClientRect().top + 44 <= markerY) active += 1;
      });
      setActiveCount((prev) => (prev === active ? prev : active));
    },
  });

  // Chapter card entrance animation — IntersectionObserver, once per card.
  // (The prototype used CSS animation-timeline: view(), which isn't
  // cross-browser yet.)
  useEffect(() => {
    const els = document.querySelectorAll('.jr-chapter');
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach(el => el.classList.add('is-visible'));
      return undefined;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -15% 0px' }
    );
    els.forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  const { meta, hero, phases, journey, thread, faq, finalCta } = home;
  useHomeMetadata(meta, faq);
  const heroPrimary = hero.primaryCta;
  const heroSecondary = hero.secondaryCta;
  const finalPrimary = finalCta.primaryCta;
  const finalSecondary = finalCta.secondaryCta;

  const scrollToAnchor = (e, anchor) => {
    e.preventDefault();
    const el = document.querySelector(anchor);
    if (!el) return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  };

  return (
    <div className="home-root min-h-screen bg-white">

      {/* Canonical marketing nav — the same bar every public page renders. */}
      <a className="home-skip-link" href="#homepage-content">Skip to content</a>
      <LandingTopNav />

      <main id="homepage-content" tabIndex={-1}>
      {/* Hero Section */}
      <section id="top" className="hero-section h-screen flex flex-col bg-gray-50">
        {/* Main content area - uses fluid container with composition lock */}
        <div className="flex-1 min-h-0 homepage-container w-full flex flex-col lg:flex-row relative">
          {/* Left: Copy */}
          <div className="hero-copy-container hero-copy text-center lg:text-left mx-auto lg:mx-0 lg:w-[45%] flex-shrink-0 pt-20 lg:pt-24 lg:self-center">
            <h1 className="hero-heading">{hero.h1}</h1>
            <p className="hero-subheading">{hero.subhead}</p>
            <div className="hero-cta-wrapper">
              <div className="hero-cta-buttons">
                <button
                  onClick={() => navigate(heroPrimary.href)}
                  className="cta-button hero-cta-button"
                >
                  <span className="relative z-10">{heroPrimary.text}</span>
                </button>
                <a
                  href={heroSecondary.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cta-button-secondary hero-cta-button-secondary"
                >
                  <span className="relative z-10">{heroSecondary.text}</span>
                </a>
              </div>
            </div>
            {/* Journey chips — each anchors to its chapter card */}
            <div className="hero-journey-chips">
              {hero.journeyChips.map((chip, i) => (
                <React.Fragment key={chip.label}>
                  {i > 0 && <span className="chip-arrow">→</span>}
                  <a href={chip.anchor} onClick={(e) => scrollToAnchor(e, chip.anchor)}>
                    {chip.label}
                  </a>
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Right: Crossfading Hero Image - fluid buffer instead of fixed */}
          <div className="hero-images-container hero-crossfade-container lg:w-[55%] lg:absolute lg:right-0 lg:top-0 lg:bottom-0">
            <div className="hero-crossfade-wrapper">
              {heroImages.map((image, index) => {
                if (index > 0 && (!heroFramesReady || reduceMotion)) return null;
                return (
                  <img
                    key={image.src}
                    src={image.src}
                    srcSet={image.srcSet}
                    sizes="(max-width: 1023px) 100vw, 60vw"
                    width={2048}
                    height={2048}
                    alt={index === currentHeroImage ? image.alt : ''}
                    onLoad={(event) => markHeroLoaded(event.currentTarget, index)}
                    className={`hero-crossfade-img ${index === currentHeroImage ? 'hero-crossfade-active' : ''}`}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fetchPriority={index === 0 ? 'high' : 'low'}
                    decoding={index === 0 ? 'sync' : 'async'}
                  />
                );
              })}
            </div>
          </div>
        </div>

        {/* Marquee Section - Inside Hero */}
        <div className="hero-marquee-section">
          <p className="hero-marquee-heading">{hero.marqueeHeading}</p>
          <div className="hero-marquee-wrapper">
            <div className="hero-marquee-track">
              {[...Array(6)].map((_, i) => (
                <img
                  key={`users-${i}`}
                  src="/assets/homepage/users-adstronautai.webp"
                  alt=""
                  className="hero-marquee-strip"
                  loading="lazy"
                />
              ))}
            </div>
          </div>
          <div className="hero-marquee-bottom-bar"></div>
        </div>
      </section>

      {/* Three phases introduce the existing journey, without a tool grid. */}
      <section id={phases.id} className="home-phases" aria-labelledby="home-phases-title">
        <div className="home-phases-inner">
          <div className="lp-section-header">
            <p className="lp-eyebrow">{phases.eyebrow}</p>
            <h2 id="home-phases-title" className="lp-h2">{phases.h2}</h2>
            <p className="lp-sub">{phases.subhead}</p>
          </div>
          <div className="home-phase-grid">
            {phases.items.map((phase) => {
              const Icon = ICON_BY_NAME[phase.icon];
              return (
                <a className="jr-card home-phase-card" key={phase.number} href={phase.anchor} onClick={(event) => scrollToAnchor(event, phase.anchor)}>
                  <span className="jr-ghost" aria-hidden="true">{phase.number}</span>
                  <div className="jr-card-content">
                    <p className="home-phase-name"><Icon size={18} strokeWidth={1.8} aria-hidden="true" />{phase.title}</p>
                    <h3>{phase.line}</h3>
                    <p className="home-phase-body">{phase.body}</p>
                    <span className="home-phase-action">{phase.linkText}<ArrowRight size={17} aria-hidden="true" /></span>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* The Journey — 6 chapters on a left rail scroller */}
      <section id="journey" className="jr-section">
        <div className="jr-inner">
          <div className="lp-section-header">
            <p className="lp-eyebrow">{journey.eyebrow}</p>
            <h2 className="lp-h2" style={{ maxWidth: '22ch' }}>{journey.h2}</h2>
            <p className="lp-sub" style={{ maxWidth: '50ch' }}>{journey.subhead}</p>
          </div>

          <div className="jr-rail" ref={railWrapRef}>
            <div className="jr-line" />
            <img
              src="/logo-black.png"
              alt=""
              className="jr-marker"
              ref={railMarkerRef}
            />

            {journey.chapters.map((ch, i) => (
              <div
                key={ch.id}
                id={ch.id}
                className="jr-chapter"
                ref={(el) => { chapterRefs.current[i] = el; }}
              >
                <div className={`jr-dot${i < activeCount ? ' active' : ''}`} />
                <div className={`jr-card${ch.featured ? ' featured' : ''}`}>
                  <div className="jr-ghost">{ch.num}</div>
                  <div className="jr-card-content">
                    <div className="jr-meta">
                      <span className="jr-stage">{ch.stage}</span>
                      {ch.tools.map((tool) => {
                        const ToolIcon = ICON_BY_TOOL[tool];
                        return (
                          <span key={tool} className={`jr-chip${ch.featured ? ' ink' : ''}`}>
                            {ToolIcon && <ToolIcon size={13} strokeWidth={2} />}
                            {tool}
                          </span>
                        );
                      })}
                      {ch.badge && (
                        <span className="jr-flame-badge">
                          <Flame size={11} fill="#F59E0B" strokeWidth={0} />
                          {ch.badge}
                        </span>
                      )}
                    </div>
                    <h3 className="jr-h3">{ch.h3}</h3>
                    <p className="jr-body">{ch.body}</p>
                    {/* Wide (2.05:1) art on desktop; a recomposed square variant
                        on phones so the story never gets letterboxed/chopped. */}
                    <picture>
                      {ch.image.mobileSrc && (
                        <source media="(max-width: 640px)" srcSet={ch.image.mobileSrc} />
                      )}
                      <img
                        src={ch.image.src}
                        alt={ch.image.alt}
                        className="jr-img"
                        style={ch.image.position ? { objectPosition: ch.image.position } : undefined}
                        loading="lazy"
                      />
                    </picture>
                    {ch.link ? (
                      <div className="jr-foot">
                        <p className="jr-caption" style={{ margin: 0 }}>{ch.caption}</p>
                        <button className="jr-link" onClick={() => navigate(ch.link.href)}>
                          {ch.link.text}
                        </button>
                      </div>
                    ) : (
                      <p className="jr-caption">{ch.caption}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <HomeThreadPreview content={thread} />

      <section className="home-faq" aria-labelledby="home-faq-title">
        <div className="home-faq-inner">
          <div>
            <p className="lp-eyebrow">{faq.eyebrow}</p>
            <h2 id="home-faq-title" className="lp-h2">{faq.h2}</h2>
          </div>
          <div className="home-faq-items">
            {faq.items.map(({ q, a }) => (
              <details key={q}>
                <summary>{q}<ChevronDown size={18} aria-hidden="true" /></summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="fc-section">
        <div className="fc-inner">
          <h2 className="lp-h2" style={{ maxWidth: '20ch', marginBottom: '14px' }}>{finalCta.h2}</h2>
          <p className="lp-sub" style={{ maxWidth: '44ch', marginBottom: '32px' }}>{finalCta.subhead}</p>
          <div className="fc-buttons">
            <div className="fc-btn-wrap">
              <button
                onClick={() => navigate(finalPrimary.href)}
                className="cta-button hero-cta-button cta-pill"
              >
                <span className="relative z-10">{finalPrimary.text}</span>
              </button>
            </div>
            <div className="fc-btn-wrap">
              <a
                href={finalSecondary.href}
                target="_blank"
                rel="noopener noreferrer"
                className="cta-button-secondary hero-cta-button-secondary cta-pill"
              >
                <span className="relative z-10">{finalSecondary.text}</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      </main>
      <LandingFooter description={home.footer.description} />
    </div>
  );
};

export default HomePage;
